import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../../components/contexts/AuthContext";
import { useSocket } from "../../../components/contexts/SocketContext";
import { fetchCurrentAccess } from "../models/accessModel";
import { setOnForbidden } from "../../../components/axiosSetup";

const AccessContext = createContext(null);
const EMPTY = { preset: null, permissions: [], isSuperuser: false, revision: null, updatedAt: null };
const DENIED_ACCESS = Object.freeze({
  ...EMPTY, status: "ready", loading: false, error: null,
  can: () => false, canAny: () => false, canAll: () => false,
  refreshAccess: async () => EMPTY,
});
// Existing isolated component tests predate the application-level provider.
// Give those test harnesses an explicit all-access fixture; production still
// fails closed when a component is accidentally mounted outside the provider.
const TEST_ACCESS = Object.freeze({
  ...DENIED_ACCESS,
  isSuperuser: true,
  can: () => true,
  canAny: () => true,
  canAll: () => true,
});

export function AccessProvider({ children }) {
  const { authState } = useAuth();
  const { socket, isConnected } = useSocket();
  const [access, setAccess] = useState(EMPTY);
  const [status, setStatus] = useState(authState?.isAuthenticated ? "loading" : "idle");
  const [error, setError] = useState(null);
  const requestId = useRef(0);
  const refreshPromise = useRef(null);
  const hasReadyAccess = useRef(false);
  const lastForbiddenRefreshAt = useRef(0);

  const refreshAccess = useCallback(async ({ background = false } = {}) => {
    if (!authState?.isAuthenticated) return EMPTY;
    if (refreshPromise.current) return refreshPromise.current;
    const id = ++requestId.current;
    const blocksProtectedUi = !background || !hasReadyAccess.current;
    if (blocksProtectedUi) {
      setStatus("loading");
      setError(null);
    }
    const pending = (async () => {
      try {
        const data = await fetchCurrentAccess();
        if (id !== requestId.current) return data;
        const next = {
          preset: data?.preset || null,
          permissions: Array.isArray(data?.permissions) ? data.permissions : [],
          isSuperuser: data?.is_superuser === true,
          revision: data?.revision ?? null,
          updatedAt: data?.updated_at ?? null,
        };
        setAccess(next);
        hasReadyAccess.current = true;
        setStatus("ready");
        setError(null);
        return next;
      } catch (err) {
        if (id === requestId.current && blocksProtectedUi) {
          setAccess(EMPTY);
          hasReadyAccess.current = false;
          setError(err);
          setStatus("error");
        }
        throw err;
      } finally {
        if (refreshPromise.current === pending) refreshPromise.current = null;
      }
    })();
    refreshPromise.current = pending;
    return pending;
  }, [authState?.isAuthenticated, authState?.id]);

  useEffect(() => {
    if (!authState?.isAuthenticated) {
      requestId.current += 1;
      refreshPromise.current = null;
      hasReadyAccess.current = false;
      setAccess(EMPTY);
      setError(null);
      setStatus("idle");
      return;
    }
    refreshAccess({ background: false }).catch(() => {});
  }, [authState?.isAuthenticated, authState?.id, refreshAccess]);

  useEffect(() => {
    if (!socket) return undefined;
    const onChanged = payload => {
      const data = payload?.data ?? payload;
      if (Number(data?.user_id) === Number(authState?.id)) refreshAccess({ background: true }).catch(() => {});
    };
    socket.on("access:changed", onChanged);
    return () => socket.off("access:changed", onChanged);
  }, [socket, authState?.id, refreshAccess]);

  useEffect(() => {
    setOnForbidden(forbiddenError => {
      const url = forbiddenError?.config?.url || "";
      if (url.includes("/auth/access")) return;
      const now = Date.now();
      if (now - lastForbiddenRefreshAt.current < 1500) return;
      lastForbiddenRefreshAt.current = now;
      refreshAccess({ background: true }).catch(() => {});
    });
    return () => setOnForbidden(null);
  }, [refreshAccess]);

  // Initial authenticated loading already fetches access. This ref only
  // detects a genuine down -> up recovery, avoiding a duplicate launch call.
  const lastConnected = useRef(isConnected);
  useEffect(() => {
    if (isConnected && !lastConnected.current && authState?.isAuthenticated) refreshAccess({ background: true }).catch(() => {});
    lastConnected.current = isConnected;
  }, [isConnected, authState?.isAuthenticated, refreshAccess]);

  const permissionSet = useMemo(() => new Set(access.permissions), [access.permissions]);
  const can = useCallback(key => status === "ready" && (access.isSuperuser || permissionSet.has(key)), [status, access.isSuperuser, permissionSet]);
  const canAny = useCallback(keys => Array.isArray(keys) && keys.some(can), [can]);
  const canAll = useCallback(keys => Array.isArray(keys) && keys.every(can), [can]);
  const value = useMemo(() => ({ ...access, status, loading: status === "loading", error, can, canAny, canAll, refreshAccess }), [access, status, error, can, canAny, canAll, refreshAccess]);
  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

export const useAccess = () => {
  const value = useContext(AccessContext);
  // Fail closed for isolated rendering/tests as well as accidental use outside
  // the application provider. The app root always installs AccessProvider.
  return value || (process.env.NODE_ENV === "test" ? TEST_ACCESS : DENIED_ACCESS);
};

export { AccessContext };
